/**
 * Jira Cloud API Emulation Plugin
 *
 * Emulates Jira's REST API v3 for sandboxed apps. Sandbox-side only — no
 * browser interception needed since Jira is an API service.
 *
 * Supports mutable state: POST creates issues that subsequent GETs return.
 * State resets on container restart.
 *
 * @module
 */

import crypto from 'node:crypto';
import type {
  EmulationPlugin,
  EmulationRequest,
  EmulationResponse,
  PluginContext,
} from '../plugin.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- parsing untrusted API input
function safeParseJson(body: string | undefined): { ok: true; data: any } | { ok: false; error: EmulationResponse } {
  try {
    return { ok: true, data: JSON.parse(body ?? '{}') };
  } catch {
    return { ok: false, error: { status: 400, body: { errorMessages: ['Invalid JSON body'] } } };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface JiraConfig {
  /** Tenant subdomain (e.g., 'acme' for acme.atlassian.net) */
  tenant: string;
}

export interface JiraUser {
  accountId: string;
  displayName: string;
  emailAddress: string;
}

export interface JiraIssue {
  id: string;
  key: string;
  projectKey: string;
  summary: string;
  description?: string;
  status: { name: string };
  assignee?: JiraUser;
  reporter?: JiraUser;
  issuetype: { name: string };
  priority?: { name: string };
  created: string;
  updated: string;
  labels?: string[];
}

export interface JiraProject {
  id: string;
  key: string;
  name: string;
}

export interface JiraFixture {
  projects: JiraProject[];
  issues: JiraIssue[];
  users?: JiraUser[];
}

export interface JiraState {
  projects: Map<string, JiraProject>;
  issues: Map<string, JiraIssue>;
  users: Map<string, JiraUser>;
  /** Per-project counter for auto-assigning issue keys */
  issueCounter: Record<string, number>;
  tenant: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Plugin
// ─────────────────────────────────────────────────────────────────────────────

export const jiraPlugin: EmulationPlugin<JiraConfig, JiraFixture, JiraState> = {
  id: 'jira',
  name: 'Jira Cloud API',
  version: '1.0.0',

  domains: (config) => [`${config.tenant}.atlassian.net`],
  needsBrowserIntercept: false,

  routes: [
    // ── Projects ─────────────────────────────────────────────────────────
    {
      method: 'GET',
      path: '/rest/api/3/project',
      handler: (_req, ctx: PluginContext<JiraFixture, JiraState>) => ({
        status: 200,
        body: Array.from(ctx.state.projects.values()),
      }),
    },
    {
      method: 'GET',
      path: '/rest/api/3/project/:projectKey',
      handler: (req, ctx: PluginContext<JiraFixture, JiraState>) => {
        const project = ctx.state.projects.get(req.params.projectKey);
        if (!project) return { status: 404, body: { errorMessages: ['Project not found'] } };
        return { status: 200, body: project };
      },
    },

    // ── Issues ───────────────────────────────────────────────────────────
    {
      method: 'GET',
      path: '/rest/api/3/issue/:issueKey',
      handler: handleGetIssue,
    },
    {
      method: 'POST',
      path: '/rest/api/3/issue',
      handler: handleCreateIssue,
    },
    {
      method: 'PUT',
      path: '/rest/api/3/issue/:issueKey',
      handler: handleUpdateIssue,
    },

    // ── Transitions ──────────────────────────────────────────────────────
    {
      method: 'GET',
      path: '/rest/api/3/issue/:issueKey/transitions',
      handler: (_req, _ctx) => ({
        status: 200,
        body: {
          transitions: [
            { id: '11', name: 'To Do' },
            { id: '21', name: 'In Progress' },
            { id: '31', name: 'Done' },
          ],
        },
      }),
    },
    {
      method: 'POST',
      path: '/rest/api/3/issue/:issueKey/transitions',
      handler: handleTransition,
    },

    // ── Search ───────────────────────────────────────────────────────────
    {
      method: 'POST',
      path: '/rest/api/3/search',
      handler: handleSearch,
    },
    {
      method: 'GET',
      path: '/rest/api/3/search',
      handler: handleSearch,
    },

    // ── Users ────────────────────────────────────────────────────────────
    {
      method: 'GET',
      path: '/rest/api/3/myself',
      handler: (_req, ctx: PluginContext<JiraFixture, JiraState>) => {
        const firstUser = ctx.state.users.values().next().value;
        if (!firstUser) return { status: 200, body: { accountId: 'emulated', displayName: 'Emulated User', emailAddress: 'emulated@test.com' } };
        return { status: 200, body: firstUser };
      },
    },

    // ── Server Info ──────────────────────────────────────────────────────
    {
      method: 'GET',
      path: '/rest/api/3/serverInfo',
      handler: (_req, _ctx) => ({
        status: 200,
        body: {
          baseUrl: 'https://emulated.atlassian.net',
          version: '1001.0.0-SNAPSHOT',
          deploymentType: 'Cloud',
          scmInfo: 'emulated',
        },
      }),
    },
  ],

  initialize(config, fixture) {
    const projects = new Map(fixture.projects.map((p) => [p.key, p]));
    const issues = new Map(fixture.issues.map((i) => [i.key, i]));
    const users = new Map((fixture.users ?? []).map((u) => [u.accountId, u]));

    const issueCounter: Record<string, number> = {};
    for (const issue of fixture.issues) {
      const num = parseInt(issue.key.split('-')[1], 10);
      if (!isNaN(num)) {
        issueCounter[issue.projectKey] = Math.max(issueCounter[issue.projectKey] ?? 0, num);
      }
    }

    return { projects, issues, users, issueCounter, tenant: config.tenant };
  },

  defaultFixture: {
    projects: [
      { id: '10000', key: 'TEST', name: 'Test Project' },
    ],
    issues: [
      {
        id: '10001',
        key: 'TEST-1',
        projectKey: 'TEST',
        summary: 'Sample issue',
        description: 'This is a sample issue for testing.',
        status: { name: 'To Do' },
        issuetype: { name: 'Task' },
        priority: { name: 'Medium' },
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
      },
    ],
    users: [
      { accountId: '100001', displayName: 'Test User', emailAddress: 'test@example.com' },
    ],
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Route Handlers
// ─────────────────────────────────────────────────────────────────────────────

function jiraBaseUrl(tenant: string): string {
  return `https://${tenant}.atlassian.net`;
}

function formatIssueResponse(issue: JiraIssue, baseUrl: string): object {
  return {
    id: issue.id,
    key: issue.key,
    self: `${baseUrl}/rest/api/3/issue/${issue.key}`,
    fields: {
      summary: issue.summary,
      description: issue.description ?? null,
      status: issue.status,
      assignee: issue.assignee ?? null,
      reporter: issue.reporter ?? null,
      issuetype: issue.issuetype,
      priority: issue.priority ?? { name: 'Medium' },
      created: issue.created,
      updated: issue.updated,
      labels: issue.labels ?? [],
      project: { key: issue.projectKey },
    },
  };
}

function handleGetIssue(
  req: EmulationRequest,
  ctx: PluginContext<JiraFixture, JiraState>
): EmulationResponse {
  const issue = ctx.state.issues.get(req.params.issueKey);
  if (!issue) return { status: 404, body: { errorMessages: [`Issue ${req.params.issueKey} not found`] } };

  const baseUrl = jiraBaseUrl(ctx.state.tenant);
  return { status: 200, body: formatIssueResponse(issue, baseUrl) };
}

function handleCreateIssue(
  req: EmulationRequest,
  ctx: PluginContext<JiraFixture, JiraState>
): EmulationResponse {
  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return parsed.error;
  const fields = parsed.data.fields ?? {};
  const projectKey = fields.project?.key;

  if (!projectKey || !ctx.state.projects.has(projectKey)) {
    return { status: 400, body: { errorMessages: [`Project ${projectKey} not found`] } };
  }

  const counter = (ctx.state.issueCounter[projectKey] ?? 0) + 1;
  ctx.state.issueCounter[projectKey] = counter;
  const key = `${projectKey}-${counter}`;

  const issue: JiraIssue = {
    id: crypto.randomUUID(),
    key,
    projectKey,
    summary: fields.summary ?? '',
    description: fields.description ?? undefined,
    status: { name: 'To Do' },
    assignee: fields.assignee ?? undefined,
    reporter: fields.reporter ?? undefined,
    issuetype: fields.issuetype ?? { name: 'Task' },
    priority: fields.priority ?? { name: 'Medium' },
    labels: fields.labels ?? [],
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
  };

  ctx.state.issues.set(key, issue);
  ctx.log('info', `Created issue ${key}: ${issue.summary}`);

  const baseUrl = jiraBaseUrl(ctx.state.tenant);
  return {
    status: 201,
    body: { id: issue.id, key, self: `${baseUrl}/rest/api/3/issue/${key}` },
  };
}

function handleUpdateIssue(
  req: EmulationRequest,
  ctx: PluginContext<JiraFixture, JiraState>
): EmulationResponse {
  const issue = ctx.state.issues.get(req.params.issueKey);
  if (!issue) return { status: 404, body: { errorMessages: [`Issue ${req.params.issueKey} not found`] } };

  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return parsed.error;
  const fields = parsed.data.fields ?? {};

  if (fields.summary !== undefined) issue.summary = fields.summary;
  if (fields.description !== undefined) issue.description = fields.description;
  if (fields.assignee !== undefined) issue.assignee = fields.assignee;
  if (fields.priority !== undefined) issue.priority = fields.priority;
  if (fields.labels !== undefined) issue.labels = fields.labels;
  issue.updated = new Date().toISOString();

  ctx.log('info', `Updated issue ${req.params.issueKey}`);
  return { status: 204 };
}

function handleTransition(
  req: EmulationRequest,
  ctx: PluginContext<JiraFixture, JiraState>
): EmulationResponse {
  const issue = ctx.state.issues.get(req.params.issueKey);
  if (!issue) return { status: 404, body: { errorMessages: [`Issue ${req.params.issueKey} not found`] } };

  const parsed = safeParseJson(req.body);
  if (!parsed.ok) return parsed.error;
  const transitionId = parsed.data.transition?.id;

  const transitionNames: Record<string, string> = { '11': 'To Do', '21': 'In Progress', '31': 'Done' };
  const newStatus = transitionNames[transitionId];
  if (newStatus) {
    issue.status = { name: newStatus };
    issue.updated = new Date().toISOString();
    ctx.log('info', `Transitioned ${req.params.issueKey} to ${newStatus}`);
  }

  return { status: 204 };
}

function handleSearch(
  req: EmulationRequest,
  ctx: PluginContext<JiraFixture, JiraState>
): EmulationResponse {
  const issues = Array.from(ctx.state.issues.values());
  const baseUrl = jiraBaseUrl(ctx.state.tenant);

  return {
    status: 200,
    body: {
      startAt: 0,
      maxResults: issues.length,
      total: issues.length,
      issues: issues.map((i) => formatIssueResponse(i, baseUrl)),
    },
  };
}
